import React, { useState, useEffect, useMemo } from 'react';
import { FileText, DollarSign, Receipt, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import localApiAdapter from '../../core/services/localApiAdapter';
import ModuleAnalyticsSummaryPage from '../../shared/components/analytics/ModuleAnalyticsSummaryPage';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { useFacturationConfig } from './useFacturationConfig';

function normalizeDocType(type) {
  return String(type || '').replace(/-/g, '_').toLowerCase();
}

function isInvoiceDoc(doc) {
  const t = normalizeDocType(doc?.type);
  return t === 'facture' || t === 'invoice';
}

function docPaymentStatus(doc) {
  return String(doc?.statut_paiement || doc?.paymentStatus || doc?.payment_status || '').toLowerCase();
}

function isPaidDoc(doc) {
  const stat = docPaymentStatus(doc);
  return stat === 'payé' || stat === 'paye' || stat === 'paid';
}

function isPartialDoc(doc) {
  const stat = docPaymentStatus(doc);
  return stat === 'partiel' || stat === 'partial';
}

function clientNameFromDoc(doc) {
  return doc?.client_nom || doc?.client_name || doc?.client || '';
}

function invoiceOutstandingTtc(doc) {
  const total = Number(doc.total ?? doc.totalAmount ?? doc.total_amount ?? doc.amount ?? 0);
  if (!(total > 0)) return 0;
  if (isPaidDoc(doc)) return 0;
  let paid = Number(doc.montant_paye ?? doc.montantPaye ?? doc.paidAmount ?? doc.montant_paid ?? 0);
  const lines = doc.paiements || doc.payments || doc.payment_installments;
  if (Array.isArray(lines) && lines.length) {
    paid = Math.max(
      paid,
      lines.reduce((sum, p) => {
        const st = String(p.status ?? p.statut ?? '').toLowerCase();
        if (st && st !== 'paid' && st !== 'payé' && st !== 'paye') return sum;
        return sum + Number(p.amount ?? p.montant ?? 0);
      }, 0)
    );
  }
  return Math.max(0, total - paid);
}

function unwrapList(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function invoiceDateRaw(doc) {
  return (
    doc?.date
    || doc?.date_facture
    || doc?.invoice_date
    || doc?.invoiceDate
    || doc?.document_date
    || doc?.created_at
    || doc?.createdAt
  );
}

function groupInvoicesByMonth(invoices) {
  const buckets = {};
  invoices.forEach((doc) => {
    const raw = invoiceDateRaw(doc);
    if (!raw) return;
    const d = new Date(raw);
    if (!Number.isFinite(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const total = Number(doc.total ?? doc.totalAmount ?? doc.total_amount ?? 0);
    buckets[key] = (buckets[key] || 0) + total;
  });
  return Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .slice(-8)
    .map(([period, value]) => ({ period, value }));
}

function agingBucket(doc) {
  const outstanding = invoiceOutstandingTtc(doc);
  if (outstanding <= 0) return 'paid';
  const dueRaw = doc?.date_echeance || doc?.due_date || doc?.dueDate;
  if (!dueRaw) return 'upcoming';
  const due = new Date(dueRaw);
  if (!Number.isFinite(due.getTime())) return 'upcoming';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today - due) / 86400000);
  if (diffDays <= 0) return 'upcoming';
  if (diffDays <= 30) return '1_30';
  if (diffDays <= 60) return '31_60';
  if (diffDays <= 90) return '61_90';
  return '90_plus';
}

const AGING_KEYS = {
  paid: 'aging_paid',
  upcoming: 'aging_upcoming',
  '1_30': 'aging_1_30',
  '31_60': 'aging_31_60',
  '61_90': 'aging_61_90',
  '90_plus': 'aging_90_plus',
};

export default function AnalysesRapportsFacturation() {
  useFacturationConfig();
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.analysesRapportsFacturation.${key}`, opts);

  const [documents, setDocuments] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const loadData = async () => {
    setLoading(true);
    try {
      const [docsRes, payRes] = await Promise.all([
        localApiAdapter.documents.getDocuments(),
        localApiAdapter.payments.getPayments(),
      ]);
      setDocuments(unwrapList(docsRes));
      setPayments(unwrapList(payRes));
    } catch (error) {
      console.error('Facturation analytics load error:', error);
      setDocuments([]);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  const invoices = useMemo(() => documents.filter(isInvoiceDoc), [documents]);

  const metrics = useMemo(() => {
    const revenue = invoices.reduce(
      (sum, doc) => sum + Number(doc.total ?? doc.totalAmount ?? doc.total_amount ?? 0),
      0
    );
    const outstanding = invoices.reduce((sum, doc) => sum + invoiceOutstandingTtc(doc), 0);
    const overdue = invoices.filter((doc) => {
      const bucket = agingBucket(doc);
      return ['1_30', '31_60', '61_90', '90_plus'].includes(bucket);
    }).length;
    const paidCount = invoices.filter(isPaidDoc).length;
    const partialCount = invoices.filter(isPartialDoc).length;
    const unpaidCount = Math.max(0, invoices.length - paidCount - partialCount);
    const openCount = invoices.filter((doc) => invoiceOutstandingTtc(doc) > 0).length;
    const collectionRate = invoices.length > 0 ? Math.round((paidCount / invoices.length) * 100) : 0;
    const avgOutstanding = openCount > 0 ? outstanding / openCount : 0;
    const overduePct = invoices.length > 0 ? Math.round((overdue / invoices.length) * 100) : 0;
    return {
      revenue,
      outstanding,
      overdue,
      paidCount,
      partialCount,
      unpaidCount,
      openCount,
      collectionRate,
      avgOutstanding,
      overduePct,
    };
  }, [invoices]);

  const insights = useMemo(
    () => [
      {
        label: p('insight_collection_rate'),
        value: loading ? '…' : `${metrics.collectionRate}%`,
      },
      {
        label: p('insight_avg_outstanding'),
        value: loading ? '…' : formatBrandMoney(metrics.avgOutstanding),
      },
      {
        label: p('insight_overdue_pct'),
        value: loading ? '…' : `${metrics.overduePct}%`,
      },
    ],
    [loading, metrics, p]
  );

  const stats = useMemo(
    () => [
      { label: p('kpi_revenue'), value: loading ? '…' : formatBrandMoney(metrics.revenue), icon: DollarSign },
      { label: p('kpi_invoice_count'), value: loading ? '…' : invoices.length, icon: FileText },
      { label: p('kpi_open_receivables_ttc'), value: loading ? '…' : formatBrandMoney(metrics.outstanding), icon: Receipt },
      { label: p('kpi_overdue_invoices'), value: loading ? '…' : metrics.overdue, icon: AlertTriangle },
    ],
    [loading, metrics, invoices.length, p]
  );

  const charts = useMemo(() => {
    const agingTotals = {};
    invoices.forEach((doc) => {
      const bucket = agingBucket(doc);
      const amount = bucket === 'paid' ? 0 : invoiceOutstandingTtc(doc);
      agingTotals[bucket] = (agingTotals[bucket] || 0) + amount;
    });
    const agingRows = Object.entries(AGING_KEYS).map(([bucket, key]) => ({
      label: p(key),
      value: agingTotals[bucket] || 0,
      display: formatBrandMoney(agingTotals[bucket] || 0),
    }));

    const monthlyRows = groupInvoicesByMonth(invoices).map((row) => ({
      label: row.period,
      value: row.value,
      display: formatBrandMoney(row.value),
    }));

    const chartList = [
      {
        title: p('chart_receivables_aging'),
        subtitle: p('chart_receivables_aging_subtitle'),
        rows: agingRows.filter((r) => r.value > 0),
      },
      {
        title: p('chart_payment_status'),
        subtitle: p('chart_payment_status_subtitle'),
        variant: 'status',
        active: metrics.paidCount,
        inactive: metrics.partialCount + metrics.unpaidCount,
      },
    ];

    if (monthlyRows.length) {
      chartList.push({
        title: p('chart_monthly_revenue'),
        subtitle: p('chart_monthly_revenue_subtitle'),
        rows: monthlyRows,
      });
    }

    return chartList;
  }, [invoices, metrics, p]);

  const table = useMemo(() => {
    const rows = (payments.length ? payments : invoices)
      .slice(0, 50)
      .map((row) => {
        const isPayment = row.invoiceNumber != null || row.invoice_number != null;
        const client = row.client || row.client_name || clientNameFromDoc(row) || p('unknown_client');
        const invoiceNo = row.invoiceNumber || row.invoice_number || row.numero || row.number || '—';
        const total = Number(
          row.totalAmount ?? row.total_amount ?? row.total ?? row.amount ?? 0
        );
        const remaining = Number(
          row.remainingAmount ?? row.remaining_amount ?? invoiceOutstandingTtc(row)
        );
        let status = p('status_unpaid');
        if (isPayment) {
          if (remaining <= 0) status = p('status_paid');
          else if (Number(row.amountPaid ?? row.amount_paid ?? 0) > 0) status = p('status_partial');
        } else if (isPaidDoc(row)) status = p('status_paid');
        else if (isPartialDoc(row)) status = p('status_partial');

        return {
          client,
          invoice_no: invoiceNo,
          amount: formatBrandMoney(total),
          outstanding: formatBrandMoney(remaining),
          status,
        };
      });

    return {
      title: p('table_invoice_register'),
      columns: [
        { key: 'client', label: p('col_client') },
        { key: 'invoice_no', label: p('col_invoice_no') },
        { key: 'amount', label: p('col_amount_ttc') },
        { key: 'outstanding', label: p('col_amount_outstanding') },
        { key: 'status', label: p('col_payment') },
      ],
      rows,
    };
  }, [payments, invoices, p]);

  return (
    <ModuleAnalyticsSummaryPage
      title={p('title')}
      subtitle={p('subtitle')}
      loading={loading}
      onRefresh={() => setRefreshTrigger((n) => n + 1)}
      stats={stats}
      insights={insights}
      charts={charts}
      table={table}
      exportFilePrefix="facturation_analyses"
      emptyLabel={p('no_data')}
    />
  );
}
